import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type FieldErrors, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Product, Variant, VariantStatus } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import type { Money } from '@nucleus-commerce/core';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  cellPath,
  fromVariants,
  toVariantsUpdate,
  variantsErrorPath,
  type VariantRowsInput,
} from '../api/variant-mappers';
import {
  variantFormSchema,
  type VariantFormInput,
  type VariantFormOutput,
} from '../schemas/variant-form-schema';
import { MASTER_ROW_FIELDS, revealFor, type VariantReveal } from '../schemas/variant-grouping';

import { useDeleteVariant, useUpdateVariants } from './use-variant-mutations';

const rowsSchema = z.object({ rows: z.record(z.string(), variantFormSchema) });
interface VariantRowsOutput {
  rows: Record<string, VariantFormOutput>;
}

type RowsForm = UseFormReturn<VariantRowsInput, unknown, VariantRowsOutput>;

/** The cells that differ from the saved row, read live: formState is a render old. */
function dirtyRowsOf(
  form: RowsForm,
): Record<string, Partial<Record<keyof VariantFormInput, true>>> {
  const dirty: Record<string, Partial<Record<keyof VariantFormInput, true>>> = {};
  for (const [id, row] of Object.entries(form.getValues('rows'))) {
    for (const field of Object.keys(row) as Array<keyof VariantFormInput>) {
      if (form.getFieldState(cellPath(id, field)).isDirty) {
        dirty[id] = { ...dirty[id], [field]: true };
      }
    }
  }
  return dirty;
}

/** Fields stay attached to their inputs through a reset, so focus and typing carry on. */
const KEEP_INPUTS = { keepFieldsRef: true } as const;

/** The first message under an error node: `weight.value` nests one level, `price.currency` too. */
function firstMessage(node: unknown): string | null {
  if (typeof node !== 'object' || node === null) return null;
  const { message } = node as { message?: unknown };
  if (typeof message === 'string') return message;
  for (const child of Object.values(node)) {
    const found = firstMessage(child);
    if (found) return found;
  }
  return null;
}

export interface UseVariantRowsFormOptions {
  /** The master fields the variant rows show without View more; an error elsewhere opens the row. */
  rowFields?: ReadonlySet<string>;
}

/**
 * The master variant fields as drafts: one form keyed by variant id, on the save bar with its own
 * entry. Save sends the changed rows and fields in one request (ADR 97), including rows whose group
 * or View more is closed: fields stay in the form when they unmount. A refused row saves nothing,
 * and `reveal` points at it. Both product pages use it; on a store's page it writes the master, so
 * it reaches every store.
 */
export function useVariantRowsForm(
  product: Product,
  variants: readonly Variant[],
  { rowFields = MASTER_ROW_FIELDS }: UseVariantRowsFormOptions = {},
) {
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const save = useUpdateVariants(product.id);
  const remove = useDeleteVariant(product.id);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Variant[]>([]);
  const [reveal, setReveal] = useState<VariantReveal | null>(null);

  const values = useMemo(() => fromVariants(variants), [variants]);
  const form = useForm<VariantRowsInput, unknown, VariantRowsOutput>({
    resolver: useI18nResolver(rowsSchema),
    defaultValues: values,
    disabled: !canWrite,
  });
  const synced = useRef(values);
  useEffect(() => {
    if (synced.current === values) return;
    synced.current = values;
    // A new list (an added or deleted variant, an options save, a refetch) becomes the saved state;
    // what the person typed on rows that are still there stays a draft that Save will send.
    const current = form.getValues('rows');
    const dirty = dirtyRowsOf(form);
    // Read out before the reset, which writes into the object `getValues` hands back.
    const typed = Object.entries(dirty).flatMap(([id, fields]) =>
      values.rows[id]
        ? (Object.keys(fields) as Array<keyof VariantFormInput>).map(
            (field) => [cellPath(id, field), current[id]?.[field]] as const,
          )
        : [],
    );
    const gone = Object.keys(current).filter((id) => !values.rows[id]);
    form.reset(values, {
      ...KEEP_INPUTS,
      keepErrors: true,
      keepIsSubmitted: true,
      keepSubmitCount: true,
    });
    for (const id of gone) form.clearErrors(`rows.${id}`);
    for (const [path, value] of typed) {
      form.setValue(path, value as never, { shouldDirty: true });
    }
  }, [form, values]);
  // The rows on screen are always rows in the form: a new row reaches the card with the sync, whose
  // reset renders this again.
  const inForm = form.getValues('rows');
  const shown = variants.filter((variant) => variant.id in inForm);
  const order = shown.map((variant) => variant.id);

  const revealFields = (fields: ReadonlyMap<string, readonly string[]>) =>
    setReveal(revealFor(order, fields, rowFields));

  const submit = form.handleSubmit(
    async (output) => {
      setSaveError(null);
      setReveal(null);
      const items = toVariantsUpdate(output.rows, dirtyRowsOf(form), order);
      if (items.length === 0) {
        form.reset(values, KEEP_INPUTS);
        return;
      }
      try {
        const saved = fromVariants(await save.mutateAsync(items));
        // A row the answer doesn't hold (an options save made it meanwhile) stays as it is.
        form.reset({ rows: { ...form.getValues('rows'), ...saved.rows } }, KEEP_INPUTS);
      } catch (caught) {
        const fields = new Map<string, string[]>();
        if (isApiError(caught)) {
          for (const detail of caught.details) {
            const path = variantsErrorPath(detail.field, items);
            if (!path) continue;
            form.setError(path, { type: 'server', message: detail.message });
            const [, id, field] = path.split('.');
            if (id && field) fields.set(id, [...(fields.get(id) ?? []), field]);
          }
        }
        if (fields.size > 0) revealFields(fields);
        else setSaveError(message(caught));
      }
    },
    (errors: FieldErrors<VariantRowsInput>) => {
      const fields = new Map(
        Object.entries(errors.rows ?? {}).map(([id, row]) => [id, Object.keys(row ?? {})]),
      );
      revealFields(fields);
    },
  );

  useSaveBar({
    form,
    onSave: submit,
    onDiscard: () => {
      setSaveError(null);
      setReveal(null);
      form.reset(values, KEEP_INPUTS);
    },
    saving: save.isPending,
    disabled: !canWrite,
  });

  const rowErrors = form.formState.errors.rows;

  return {
    form,
    canWrite,
    /** `variants`, less a row the form doesn't hold yet. */
    shown,
    reveal,
    /** Rows holding an error. */
    errorIds: new Set(Object.keys(rowErrors ?? {})),
    /** The message of an error on a field the row itself doesn't show. */
    hiddenError: (id: string) => {
      const row = rowErrors?.[id];
      if (!row) return null;
      for (const [field, node] of Object.entries(row)) {
        if (rowFields.has(field)) continue;
        const text = firstMessage(node);
        if (text) return text;
      }
      return null;
    },
    /** A failed save or delete that belongs to no field. */
    problems: [saveError, deleteError].filter((text): text is string => Boolean(text)),
    /** The SKU as typed, for the search. */
    skuOf: (variant: Variant) => form.getValues(cellPath(variant.id, 'sku')),
    setStatus: (rows: readonly Variant[], status: VariantStatus) => {
      for (const row of rows) {
        form.setValue(cellPath(row.id, 'status'), status, { shouldDirty: true });
      }
    },
    setPrice: (rows: readonly Variant[], price: Money) => {
      for (const row of rows) {
        form.setValue(cellPath(row.id, 'price'), price, { shouldDirty: true });
      }
    },
    deleting,
    removing: remove.isPending,
    requestDelete: setDeleting,
    cancelDelete: () => setDeleting([]),
    /** Deletes one by one; those refused stay, and say why. */
    confirmDelete: async () => {
      setDeleteError(null);
      const failed: Array<{ title: string; reason: string }> = [];
      for (const variant of deleting) {
        try {
          await remove.mutateAsync(variant.id);
        } catch (caught) {
          failed.push({ title: variant.title, reason: message(caught) });
        }
      }
      setDeleting([]);
      return failed;
    },
    setDeleteError,
  };
}

export type VariantRowsForm = ReturnType<typeof useVariantRowsForm>;
